import React, { useState, useEffect } from 'react';

import {
  Container,
  Typography,
  List,
  ListItem,
  Divider,
  Button,
  Box,
  TextField,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@mui/material';
import { Link } from 'react-router-dom';
import OrderSummary from './OrderSummary';
import { useCart } from '../context/CartContext';
import { useProducts } from '../context/ProductsContext';
import { useAuth } from '../context/AuthContext';
import { getColorLabel } from '../utils/colors';
import AddressInput, { AddressValue } from './AddressInput';
import { fetchSizeStockForProducts, SizeStockMap } from '../extraApi';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

// Le paiement est géré via Stripe Checkout (redirection vers une page Stripe)

const Cart = () => {
  const { items: cartItems, updateItemQuantity, removeItem, total } = useCart();

  const { products } = useProducts();

  const { user, token } = useAuth();

  const [sizeStockByProduct, setSizeStockByProduct] = useState<
    Record<number, SizeStockMap>
  >({});

  const [promoCode, setPromoCode] = useState('');
  const [promoPercent, setPromoPercent] = useState<number | null>(null);
  const [promoCodeApplied, setPromoCodeApplied] = useState<string | null>(null);
  const [promoError, setPromoError] = useState<string | null>(null);
  const [applyingPromo, setApplyingPromo] = useState(false);

  const [shippingPostalCode, setShippingPostalCode] = useState('');
  const [shippingCity, setShippingCity] = useState('');
  const [shippingStreet, setShippingStreet] = useState('');
  const [shippingHouseNumber, setShippingHouseNumber] = useState('');

  const [shippingDistance, setShippingDistance] = useState<'short' | 'medium' | 'long' | ''>('');
  const [shippingMode, setShippingMode] = useState<'free' | 'express' | 'relay' | 'jls' | null>(null);
  const [shippingCost, setShippingCost] = useState<number | null>(null);
  const [shippingMessage, setShippingMessage] = useState<string | null>(null);
  const [shippingError, setShippingError] = useState<string | null>(null);

  // Pré-remplit l'adresse de livraison avec l'adresse du profil, si disponible
  useEffect(() => {
    if (!user) return;

    const hasShippingAddress =
      shippingStreet.trim() !== '' ||
      shippingPostalCode.trim() !== '' ||
      shippingCity.trim() !== '';

    if (hasShippingAddress) {
      return;
    }

    const profileLine1 = (user.address_line1 as string | undefined) || '';
    const profilePostalCode = (user.postal_code as string | undefined) || '';
    const profileCity = (user.city as string | undefined) || '';

    if (profileLine1 || profilePostalCode || profileCity) {
      setShippingStreet(profileLine1);
      setShippingPostalCode(profilePostalCode);
      setShippingCity(profileCity);
    }
  }, [user, shippingStreet, shippingPostalCode, shippingCity]);

  useEffect(() => {
    const ids = Array.from(new Set(cartItems.map((item) => item.id))).filter((id) =>
      Number.isFinite(id)
    );

    if (ids.length === 0) {
      setSizeStockByProduct({});
      return;
    }

    let cancelled = false;

    const loadSizeStock = async () => {
      try {
        const data = await fetchSizeStockForProducts(ids as number[]);
        if (!cancelled) {
          setSizeStockByProduct(data);
        }
      } catch (e) {
        console.error(
          'Erreur lors du chargement du stock par taille pour le panier (mini backend)',
          e
        );
      }
    };

    void loadSizeStock();

    return () => {
      cancelled = true;
    };
  }, [cartItems]);

  useEffect(() => {
    const postalCode = (shippingPostalCode ?? '').toString().trim();
    const city = (shippingCity ?? '').toString().trim();
    const street = (shippingStreet ?? '').toString().trim();

    // On calcule la zone de livraison dès que le code postal, la ville et la rue sont présents.
    // Le numéro de maison reste facultatif pour le calcul/mode de livraison.
    if (!postalCode || !city || !street) {
      setShippingError(null);
      setShippingMessage(null);
      setShippingCost(null);
      setShippingDistance('');
      setShippingMode(null);

      return;
    }

    setShippingError(null);
    setShippingMessage(null);
    setShippingCost(null);

    const zoneACodes = ['4500', '4520', '4530', '4540', '4570'];
    const zoneBCodes = [
      '4537',
      '4470',
      '4480',
      '4550',
      '4557',
      '4577',
      '4560',
      '4217',
      '4210',
      '4260',
      '5300',
      '5352',
      '5370',
    ];

    let distanceCategory: 'short' | 'medium' | 'long' = 'long';

    if (zoneACodes.includes(postalCode)) {
      distanceCategory = 'short';
    } else if (zoneBCodes.includes(postalCode)) {
      distanceCategory = 'medium';
    } else {
      distanceCategory = 'long';
    }

    setShippingDistance(distanceCategory);

    if (distanceCategory === 'short') {
      setShippingMode('free');
      setShippingCost(0);
      setShippingMessage(
        'Vous êtes éligible à la livraison gratuite (24–72h). Vous pouvez aussi choisir la livraison express (24–48h) à 4,50 €.'
      );
    } else if (distanceCategory === 'medium') {
      setShippingMode('express');
      setShippingCost(4.5);
      setShippingMessage(
        'Pour cette distance, la livraison express (24–48h) à 4,50 € est disponible. La livraison gratuite n’est pas disponible.'
      );
    } else if (distanceCategory === 'long') {
      setShippingMode('relay');
      setShippingCost(5);
      setShippingMessage(null);
    }
  }, [shippingPostalCode, shippingCity, shippingStreet]);

  const handleUpdateQuantity = (
    item: import('../context/CartContext').CartItem,
    newQuantity: number
  ) => {
    if (newQuantity < 1) return;

    let maxQuantity: number | null = null;

    if (item && item.size) {
      const sizeMap = sizeStockByProduct[item.id];
      const perSize = sizeMap ? sizeMap[item.size] : undefined;
      if (typeof perSize === 'number' && Number.isFinite(perSize) && perSize >= 0) {
        maxQuantity = perSize;
      }
    }

    if (maxQuantity == null) {
      const product = products.find((p) => p.id === item.id);
      if (product && product.stock != null) {
        maxQuantity = product.stock;
      }
    }

    if (maxQuantity != null && newQuantity > maxQuantity) {
      newQuantity = maxQuantity;
    }

    if (newQuantity < 1) return;
    updateItemQuantity(item.id, item.size, item.color, newQuantity);
  };

  const handleRemoveItem = (item: import('../context/CartContext').CartItem) => {
    removeItem(item.id, item.size, item.color);
  };

  const handleApplyPromo = async () => {
    const code = promoCode.trim();
    if (!code) return;

    setPromoError(null);
    setApplyingPromo(true);

    try {
      const response = await fetch(`${API_BASE_URL}/promo-codes/validate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ code }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => null);
        const message = data && data.error ? data.error : 'Code promo invalide ou expiré.';
        setPromoError(message);
        setPromoPercent(null);
        setPromoCodeApplied(null);
        return;
      }

      const data = await response.json();
      const percent =
        typeof data.discount_percent === 'number'
          ? data.discount_percent
          : Number(data.discount_percent) || 0;

      if (!Number.isFinite(percent) || percent <= 0) {
        setPromoError('Code promo invalide.');
        setPromoPercent(null);
        setPromoCodeApplied(null);
        return;
      }

      setPromoPercent(percent);
      setPromoCodeApplied(data.code || code.toUpperCase());
      setPromoError(null);
    } catch (e) {
      console.error('Erreur lors de la validation du code promo', e);
      setPromoError('Erreur réseau lors de la validation du code promo.');
      setPromoPercent(null);
      setPromoCodeApplied(null);
    } finally {
      setApplyingPromo(false);
    }
  };

  const handleClearPromo = () => {
    setPromoPercent(null);
    setPromoCodeApplied(null);
    setPromoError(null);
    setPromoCode('');
  };

  const handleCheckout = async () => {
    if (cartItems.length === 0) return;

    const postalCode = (shippingPostalCode ?? '').toString().trim();
    const city = (shippingCity ?? '').toString().trim();
    const street = (shippingStreet ?? '').toString().trim();
    const numericShipping =
      typeof shippingCost === 'number' && Number.isFinite(shippingCost);
    const hasShippingDistance =
      shippingDistance === 'short' ||
      shippingDistance === 'medium' ||
      shippingDistance === 'long';

    const shippingReady =
      postalCode &&
      city &&
      street &&
      hasShippingDistance &&
      shippingMode !== null &&
      (shippingDistance === 'long' || numericShipping);

    if (!shippingReady) {
      setShippingError(
        "Merci de renseigner votre adresse complète et de choisir un mode de livraison avant de passer la commande."
      );
      return;
    }

    setShippingError(null);

    try {
      const promoDiscountNow =
        promoPercent && promoPercent > 0
          ? (total * promoPercent) / 100
          : 0;
      const shippingAmountNow =
        typeof shippingCost === 'number' && Number.isFinite(shippingCost)
          ? shippingCost
          : 0;
      const finalTotal = total - promoDiscountNow + shippingAmountNow;

      const pendingOrder = {
        items: cartItems,
        total: finalTotal,
        date: new Date().toISOString(),
        authToken: token || null,
        userId: user?.id ?? null,
        userEmail: user?.email ?? null,
        shippingAmount: shippingAmountNow,
        shippingMode,
        shippingPostalCode,
        shippingCity,
        shippingStreet,
        shippingHouseNumber,
      };
      sessionStorage.setItem('pending_order', JSON.stringify(pendingOrder));
      localStorage.setItem('pending_order_backup', JSON.stringify(pendingOrder));
    } catch (e) {
      console.error('Erreur lors de la sauvegarde de la commande en attente', e);
    }

    try {
      const payload: any = { items: cartItems };

      if (promoPercent && promoPercent > 0) {
        payload.promoPercent = promoPercent;
      }

      const shippingAmountNow =
        typeof shippingCost === 'number' && Number.isFinite(shippingCost)
          ? shippingCost
          : 0;

      if (shippingAmountNow > 0) {
        payload.shippingAmount = shippingAmountNow;
      }

      if (user) {
        // Utilise l'adresse saisie dans le panier pour Stripe
        payload.customer = {
          email: user.email,
          name: user.name,
          phone: user.phone,
          address: {
            line1: shippingStreet || undefined,
            line2: shippingHouseNumber || undefined,
            postal_code: shippingPostalCode || undefined,
            city: shippingCity || undefined,
            country: user.country || 'BE',
          },
        };
      }

      const response = await fetch(`${API_BASE_URL}/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        console.error("Erreur lors de la création de la session de paiement");
        return;
      }

      const data = await response.json();
      if (data && data.url) {
        window.location.href = data.url as string;
      } else {
        console.error('URL de session Stripe manquante dans la réponse du serveur');
      }
    } catch (e) {
      console.error('Erreur lors de la redirection vers Stripe Checkout', e);
    }
  };

  const promoDiscount =
    promoPercent && promoPercent > 0
      ? (total * promoPercent) / 100
      : 0;

  const hasNumericShipping =
    typeof shippingCost === 'number' && Number.isFinite(shippingCost);

  const isShippingAddressComplete =
    (shippingPostalCode ?? '').toString().trim() !== '' &&
    (shippingCity ?? '').toString().trim() !== '' &&
    (shippingStreet ?? '').toString().trim() !== '';

  const hasShippingDistance =
    shippingDistance === 'short' ||
    shippingDistance === 'medium' ||
    shippingDistance === 'long';

  const isShippingReady =
    isShippingAddressComplete &&
    hasShippingDistance &&
    shippingMode !== null &&
    (shippingDistance === 'long' || hasNumericShipping);

  const canCheckout = cartItems.length > 0 && isShippingReady;

  const handleAddressChange = (addr: AddressValue) => {
    setShippingStreet(addr.addressLine1 || '');
    setShippingPostalCode(addr.postalCode || '');
    setShippingCity(addr.city || '');
    setShippingHouseNumber(addr.addressLine2 || '');
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Votre Panier
      </Typography>

      {cartItems.length === 0 ? (
        <Box textAlign="center" my={8}>
          <Typography variant="h6" gutterBottom>
            Votre panier est vide
          </Typography>
          <Button
            variant="contained"
            color="primary"
            component={Link}
            to="/products"
            sx={{ mt: 2 }}
          >
            Voir les produits
          </Button>
        </Box>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gap: 4,
            gridTemplateColumns: { xs: '1fr', md: '1fr' },
            alignItems: 'flex-start',
          }}
        >
          {/* Colonne gauche : liste des articles */}
          <Box sx={{ width: '100%' }}>
            <List sx={{ bgcolor: 'background.paper', borderRadius: 1 }}>
              {cartItems.map((item, index) => {
                const product = products.find((p) => p.id === item.id);

                const sizeMap = sizeStockByProduct[item.id];
                let maxQuantity: number | undefined;
                if (item.size && sizeMap && typeof sizeMap[item.size] === 'number') {
                  const perSize = sizeMap[item.size] as number;
                  if (Number.isFinite(perSize) && perSize >= 0) {
                    maxQuantity = perSize;
                  }
                }

                const canIncrease =
                  maxQuantity == null || item.quantity < maxQuantity;

                return (
                  <React.Fragment
                    key={`${item.id}-${item.size || 'no-size'}-${
                      item.color || 'no-color'
                    }`}
                  >
                    <ListItem
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: { xs: 'flex-start', sm: 'center' },
                        gap: { xs: 1.5, sm: 0 },
                      }}
                    >
                      {/* Bloc texte : titre + détails */}
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          flex: 1,
                          minWidth: 0,
                        }}
                      >
                        <Typography variant="body1">{item.name}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {item.price.toFixed(2)} €
                        </Typography>
                        {(item.size || item.color) && (
                          <Typography variant="body2" color="text.secondary">
                            {item.size && `Taille : ${item.size}`}
                            {item.size && item.color && ' • '}
                            {item.color && `Couleur : ${getColorLabel(item.color)}`}
                          </Typography>
                        )}
                        {(() => {
                          const sizeMapLocal = sizeStockByProduct[item.id];
                          if (item.size && sizeMapLocal && typeof sizeMapLocal[item.size] === 'number') {
                            const perSize = sizeMapLocal[item.size] as number;
                            if (Number.isFinite(perSize) && perSize >= 0) {
                              return (
                                <Typography variant="caption" color="text.secondary">
                                  Stock restant pour taille {item.size} : {perSize}
                                </Typography>
                              );
                            }
                          }

                          if (product && product.stock != null) {
                            return (
                              <Typography variant="caption" color="text.secondary">
                                Stock restant : {product.stock}
                              </Typography>
                            );
                          }

                          return null;
                        })()}
                      </Box>

                      {/* Bloc commandes : quantité / prix / supprimer */}
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: { xs: 'column', sm: 'row' },
                          alignItems: { xs: 'flex-start', sm: 'center' },
                          justifyContent: { xs: 'flex-start', sm: 'flex-end' },
                          mt: 0,
                          gap: { xs: 1, sm: 2 },
                          width: 'auto',
                          ml: { xs: 2, sm: 3 },
                        }}
                      >
                        {/* Ligne 1 : - quantité + */}
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                          }}
                        >
                          <Button
                            size="small"
                            onClick={() =>
                              handleUpdateQuantity(item, item.quantity - 1)
                            }
                            disabled={item.quantity <= 1}
                          >
                            -
                          </Button>
                          <Typography
                            sx={{
                              minWidth: 32,
                              textAlign: 'center',
                            }}
                          >
                            {item.quantity}
                          </Typography>
                          <Button
                            size="small"
                            onClick={() =>
                              handleUpdateQuantity(item, item.quantity + 1)
                            }
                            disabled={!canIncrease}
                          >
                            +
                          </Button>
                        </Box>

                        {/* Ligne 2 : total + bouton Supprimer */}
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            ml: { xs: 0, sm: 4 },
                            mt: { xs: 0.5, sm: 0 },
                            width: { xs: '100%', sm: 'auto' },
                            justifyContent: {
                              xs: 'space-between',
                              sm: 'flex-end',
                            },
                          }}
                        >
                          <Typography
                            sx={{
                              minWidth: '80px',
                              textAlign: 'right',
                            }}
                          >
                            {(item.price * item.quantity).toFixed(2)} €
                          </Typography>
                          <Button
                            color="error"
                            size="small"
                            onClick={() => handleRemoveItem(item)}
                          >
                            Supprimer
                          </Button>
                        </Box>
                      </Box>
                    </ListItem>
                    {index < cartItems.length - 1 && <Divider component="li" />}
                  </React.Fragment>
                );
              })}
            </List>
          </Box>

          {/* Colonne droite : Livraison + Code promo (layout responsive), puis récap + boutons */}
          <Box sx={{ width: '100%' }}>
            <Box
              sx={{
                mt: 3,
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr' },
                gap: 2,
              }}
            >
              {/* Bloc Livraison */}
              <Box
                sx={{
                  p: 2.5,
                  bgcolor: 'background.paper',
                  borderRadius: 1,
                  boxShadow: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                }}
              >
                <Typography variant="subtitle1">Livraison</Typography>
                <Typography variant="body2" color="text.secondary">
                  Indiquez votre adresse complète pour la livraison. Vous pouvez rechercher
                  votre adresse ou la saisir manuellement (la rue sera automatiquement sans
                  accents pour faciliter le traitement par les systèmes de livraison).
                </Typography>
                <AddressInput
                  value={{
                    addressLine1: shippingStreet,
                    addressLine2: shippingHouseNumber,
                    city: shippingCity,
                    postalCode: shippingPostalCode,
                    country: 'BE',
                    isManual: false,
                  }}
                  onChange={handleAddressChange}
                  label="Adresse de livraison"
                />

                {shippingError && (
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{ display: 'block', mt: 0.5 }}
                  >
                    {shippingError}
                  </Typography>
                )}

                {shippingDistance === 'short' && (
                  <FormControl component="fieldset" sx={{ mt: 1 }}>
                    <FormLabel component="legend">Choix du mode de livraison</FormLabel>
                    <RadioGroup
                      value={shippingMode || 'free'}
                      onChange={(e) => {
                        const value = e.target.value as 'free' | 'express';
                        setShippingMode(value);
                        if (value === 'free') {
                          setShippingCost(0);
                        } else if (value === 'express') {
                          setShippingCost(4.5);
                        }
                      }}
                    >
                      <FormControlLabel
                        value="free"
                        control={<Radio size="small" />}
                        label="Livraison gratuite (24–72h)"
                      />
                      <FormControlLabel
                        value="express"
                        control={<Radio size="small" />}
                        label="Livraison express (24–48h) – 4,50 €"
                      />
                    </RadioGroup>
                  </FormControl>
                )}

                {shippingDistance === 'medium' && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Mode sélectionné : Livraison express (24–48h) – 4,50 €.
                  </Typography>
                )}

                {shippingDistance === 'long' && (
                  <FormControl component="fieldset" sx={{ mt: 1 }}>
                    <FormLabel component="legend">Choix du mode de livraison</FormLabel>
                    <RadioGroup
                      value={shippingMode || 'relay'}
                      onChange={(e) => {
                        const value = e.target.value as 'relay' | 'jls';
                        setShippingMode(value);
                        if (value === 'relay') {
                          setShippingCost(5);
                        } else {
                          setShippingCost(6.6);
                        }
                      }}
                    >
                      <FormControlLabel
                        value="relay"
                        control={<Radio size="small" />}
                        label="Point relais – 5,00 €"
                      />
                      <FormControlLabel
                        value="jls"
                        control={<Radio size="small" />}
                        label="Livraison à domicile GLS – 6,60 €"
                      />
                    </RadioGroup>
                  </FormControl>
                )}

                {shippingDistance !== 'long' && shippingCost != null && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Estimation des frais de livraison : {shippingCost.toFixed(2)} €
                  </Typography>
                )}

                {shippingMode && shippingCost == null && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Les frais exacts seront calculés ultérieurement pour ce transporteur.
                  </Typography>
                )}
              </Box>

              {/* Bloc Code promo */}
              <Box
                sx={{
                  p: 2.5,
                  bgcolor: 'background.paper',
                  borderRadius: 1,
                  boxShadow: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                }}
              >
                <Typography variant="subtitle2">Code promo</Typography>
                <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                  <TextField
                    size="small"
                    placeholder="Ex: BRATAN10"
                    value={promoCode}
                    onChange={(event) => setPromoCode(event.target.value.toUpperCase())}
                    sx={{ minWidth: 180, flexGrow: 1 }}
                  />
                  <Button
                    variant="outlined"
                    onClick={handleApplyPromo}
                    disabled={!promoCode || applyingPromo}
                  >
                    Appliquer
                  </Button>
                  {promoPercent && promoPercent > 0 && (
                    <Button variant="text" color="secondary" onClick={handleClearPromo}>
                      Supprimer le code
                    </Button>
                  )}
                </Box>
                {promoPercent && promoPercent > 0 && promoCodeApplied && (
                  <Typography
                    variant="caption"
                    color="success.main"
                    sx={{ display: 'block', mt: 0.5 }}
                  >
                    Code {promoCodeApplied} appliqué : -{promoPercent.toFixed(0)}%
                  </Typography>
                )}
                {promoError && (
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{ display: 'block', mt: 0.5 }}
                  >
                    {promoError}
                  </Typography>
                )}
              </Box>

              {/* Bloc Récapitulatif (OrderSummary) */}
              <Box
                sx={{
                  gridColumn: { xs: '1 / -1', md: '1 / 2' },
                  p: 2.5,
                  bgcolor: 'background.paper',
                  borderRadius: 1,
                  boxShadow: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                  mt: 1,
                }}
              >
                <OrderSummary
                  items={cartItems}
                  promoLabel={
                    promoPercent && promoPercent > 0 && promoCodeApplied
                      ? `Code promo (${promoCodeApplied})`
                      : undefined
                  }
                  promoAmount={promoDiscount}
                  shippingLabel={hasNumericShipping ? 'Frais de livraison' : undefined}
                  shippingAmount={hasNumericShipping ? shippingCost || 0 : undefined}
                />
              </Box>
            </Box>

            <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Button
                component={Link}
                to="/products"
                variant="outlined"
                fullWidth
              >
                Continuer mes achats
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleCheckout}
                fullWidth
                size="large"
                disabled={!canCheckout}
              >
                Passer la commande
              </Button>
            </Box>
          </Box>
        </Box>
      )}
    </Container>
  );
};

export default Cart;
